/**
 * AVISO — a mensagem rápida, chamável de qualquer lugar
 *
 * ── POR QUE ISTO EXISTE ──────────────────────────────────────
 * O sistema tem um Toast desde 25/07/2026, e mesmo assim havia 188
 * `alert()` nativos espalhados por 38 telas. Nenhuma delas usava o
 * Toast.
 *
 * O motivo não era desleixo: `useToast` é um HOOK. Para usá-lo, a tela
 * precisa importar, chamar o hook e renderizar `ToastComponent` no
 * lugar certo do JSX. Três passos, em cada arquivo. Diante de um
 * `alert('Nome obrigatório.')` de uma linha, ninguém faz os três.
 *
 * Enquanto o caminho certo custar mais que o errado, o errado ganha.
 * Então o caminho certo passou a custar uma linha:
 *
 *     import { avisar } from '../../components/aviso';
 *     avisar('Nome obrigatório.');
 *
 * ── POR QUE DOM PURO, E NÃO REACT ────────────────────────────
 * Um componente React precisaria de contexto ou de estar montado em
 * algum lugar da árvore — e voltaríamos ao problema do plumbing. Aqui
 * o container se cria sozinho no primeiro uso, vive no `body`, e a
 * função pode ser chamada de dentro de um `catch`, de um utilitário ou
 * de código que nem é componente.
 *
 * O `useToast` continua existindo e válido: quando a tela quer
 * controlar o momento e o lugar da mensagem, ele é melhor. Isto aqui é
 * para o caso comum, que é a maioria.
 *
 * ── O VISUAL É O MESMO ───────────────────────────────────────
 * Usa `.toast-flutuante` e `data-tipo`, as mesmas classes do Toast.
 * Nenhum estilo novo — se a diretriz visual mudar, muda nos dois.
 */

const DURACAO = 3200;
let container = null;

function garantirContainer() {
  if (container && document.body.contains(container)) return container;
  container = document.createElement('div');
  container.className = 'aviso-pilha';
  /* Empilha de baixo para cima e não intercepta clique: a mensagem
     não pode bloquear o que está atrás dela — foi justamente esse o
     problema do `alert`, que trava a tela até alguém clicar em OK. */
  container.style.cssText = [
    'position:fixed', 'left:0', 'right:0', 'bottom:24px', 'z-index:4000',
    'display:flex', 'flex-direction:column-reverse', 'align-items:center',
    'gap:8px', 'pointer-events:none',
  ].join(';');
  document.body.appendChild(container);
  return container;
}

/* (V1918, a demo aprovada demo-v1918-avisos-e-acervo.html) OS AVISOS NUMA PEÇA SÓ: a do aviso do excluir (a superfície invertida, o canto
   de 18, a sombra) para todos — o sucesso, o erro e o do excluir. O ícone é o do sistema (o mesmo traço do ⋯) num círculo; o erro fica até
   tocar (com o Fechar), o sucesso some sozinho. No celular a pilha sobe para logo ACIMA do botão do pé (quando há um), e não cobre ele. */
const SV = (d) => `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${d}</svg>`;
export const ICONES_AVISO = {
  success: SV('<path d="M5 12l4 4 10-10"/>'),
  error: SV('<circle cx="12" cy="12" r="8.5"/><path d="M12 7.5v5"/><path d="M12 16h.01"/>'),
  excluido: SV('<path d="M4 7h16M9 7V4.5h6V7"/><path d="M6.5 7l1 12.5h9l1-12.5"/>'),
  rede: SV('<path d="M3 3l18 18"/><path d="M8.5 8.6A5.5 5.5 0 0 0 6.5 13H6a4 4 0 0 0 0 8h11"/><path d="M12.4 6.1A5.5 5.5 0 0 1 18 11h.5a3.5 3.5 0 0 1 2.1 6.3"/>'),
};
/* a altura do botão do pé no celular (para a pilha ficar acima dele) */
function acimaDoPe() {
  const pe = document.querySelector('.sl-pe'); if (!pe || window.matchMedia('(min-width: 900px)').matches) return 24;
  const r = pe.getBoundingClientRect(); return r.height > 0 && r.top < window.innerHeight ? Math.round(window.innerHeight - r.top + 8) : 24;
}
function sair(el) {
  el.style.transition = 'opacity .2s var(--ease-saida), transform .2s var(--ease-saida)';
  el.style.opacity = '0'; el.style.transform = 'translateY(6px)';
  setTimeout(() => el.remove(), 220);
}

/**
 * @param {string} texto    o que dizer — frase curta, já em português
 * @param {'success'|'error'} tipo
 */
export function avisar(texto, tipo = 'error') {
  if (!texto) return;
  const pilha = garantirContainer(); pilha.style.bottom = `${acimaDoPe()}px`;
  const erro = tipo !== 'success';
  const el = document.createElement('div');
  el.className = `aviso-v2${erro ? ' erro' : ''}`; el.style.pointerEvents = 'auto';
  el.setAttribute('role', erro ? 'alert' : 'status'); el.setAttribute('aria-live', erro ? 'assertive' : 'polite');
  const linha = document.createElement('div'); linha.className = 'aviso-v2-l';
  const icone = document.createElement('span'); icone.className = 'aviso-v2-i'; icone.innerHTML = erro ? ICONES_AVISO.error : ICONES_AVISO.success;
  /* `textContent`, nunca `innerHTML`: a mensagem pode vir de `err.response.data.erro`, ou seja, de fora. */
  const txt = document.createElement('span'); txt.className = 'aviso-v2-t'; txt.textContent = texto;
  linha.append(icone, txt);
  if (erro) { const bt = document.createElement('button'); bt.type = 'button'; bt.className = 'aviso-v2-d'; bt.textContent = 'Fechar'; bt.onclick = () => sair(el); linha.appendChild(bt); }
  el.appendChild(linha);
  if (!erro) el.addEventListener('click', () => sair(el));
  pilha.appendChild(el);
  if (!erro) setTimeout(() => sair(el), DURACAO);   // o erro fica até tocar
}

/* (V1907, a demo aprovada "a carcaça v2") O AVISO DEPOIS DE EXCLUIR: o que foi e para onde, com "Desfazer" e a barra do tempo. O "Desfazer"
   chama a volta curta da lixeira (só quem excluiu, nos primeiros 60 s); sem o número da lixeira, ele não aparece (não promete o que não existe).
   A mesma pilha do avisar; o desenho é o da demo (a pílula escura, a barra embaixo). */
const DURACAO_EXCLUIDO = 8000;
export function avisarExcluido({ texto, sub = '', lixeiraId = null, API = null, aoDesfeito = null, desfazer = null, simbolo = 'excluido' }) {   // (V1927) simbolo: o aviso que não é de excluir vem sem a lixeira (null), como o da demo do recebimento   // (V1909) desfazer: o Desfazer de algo que não foi para a lixeira (o riscado do Diário de Bordo)
  if (!texto) return;
  const pilha = garantirContainer(); pilha.style.bottom = `${acimaDoPe()}px`;   // (V1918) acima do botão do pé no celular
  const el = document.createElement('div');
  el.className = 'aviso-v2'; el.style.pointerEvents = 'auto'; el.setAttribute('role', 'status'); el.setAttribute('aria-live', 'polite');
  const linha = document.createElement('div'); linha.className = 'aviso-v2-l';
  const icone = document.createElement('span'); icone.className = 'aviso-v2-i';
  icone.innerHTML = simbolo ? ICONES_AVISO[simbolo] || ICONES_AVISO.excluido : '';   // (V1918) a lixeira do ⋯ · (V1927) ou nenhum
  const txt = document.createElement('span'); txt.className = 'aviso-v2-t'; txt.textContent = texto;
  if (sub) { const s2 = document.createElement('small'); s2.textContent = sub; txt.appendChild(s2); }
  if (simbolo) linha.append(icone, txt); else linha.append(txt);
  const fechar = () => { el.style.transition = 'opacity .2s, transform .2s'; el.style.opacity = '0'; el.style.transform = 'translateY(6px)'; setTimeout(() => el.remove(), 220); };
  if (desfazer && !lixeiraId) {
    const bt = document.createElement('button'); bt.type = 'button'; bt.className = 'aviso-v2-d'; bt.textContent = 'Desfazer';
    bt.addEventListener('click', async (e) => { e.stopPropagation(); bt.disabled = true;
      try { await desfazer(); el.remove(); } catch (err) { el.remove(); avisar(err?.response?.data?.erro || 'Não foi possível desfazer.'); } });
    linha.appendChild(bt);
  }
  if (lixeiraId && API) {
    const bt = document.createElement('button'); bt.type = 'button'; bt.className = 'aviso-v2-d'; bt.textContent = 'Desfazer';
    bt.addEventListener('click', async (e) => { e.stopPropagation(); bt.disabled = true; bt.textContent = 'Desfazendo…';
      try { await API.post(`/lixeira/${lixeiraId}/desfazer`); el.remove(); avisarOk('Desfeito: voltou como estava.'); aoDesfeito?.(); }
      catch (err) { el.remove(); avisar(err.response?.data?.erro || 'Não foi possível desfazer.'); } });
    linha.appendChild(bt);
  }
  const barra = document.createElement('span'); barra.className = 'aviso-v2-b'; barra.style.animationDuration = `${DURACAO_EXCLUIDO}ms`;
  el.append(linha, barra); pilha.appendChild(el);
  setTimeout(() => { if (document.body.contains(el)) fechar(); }, DURACAO_EXCLUIDO);
}

/** Atalho para o caso de sucesso, que é o menos comum. */
export const avisarOk = (texto) => avisar(texto, 'success');

export default avisar;
